import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToaster } from "../context/ToasterContext";
import Services from "../services/Services";
import { MeMeetApp } from "../components";
import "./MeMeetPage.css";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const isTruthy = (v) => v === true || v === 1 || v === "1";

function normalizeChecklistResponse(res) {
  try {
    const raw = res?.data;
    if (raw && typeof raw === "object") {
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw.result)) return raw.result;
      return [];
    }
    if (typeof raw !== "string") return [];
    const cleaned = raw.replace(/<[^>]*>/g, "").trim();
    const jsonStart = cleaned.indexOf("{");
    if (jsonStart === -1) return [];
    const parsed = JSON.parse(cleaned.slice(jsonStart));
    return Array.isArray(parsed?.result) ? parsed.result : [];
  } catch {
    return [];
  }
}

function normalizeNoteList(res) {
  const result = res?.data?.result;
  if (!result) return [];
  const folderNotes =
    result.folders?.flatMap(
      (folder) =>
        folder.notes?.map((note) => ({
          ...note,
          folderTitle: folder.title,
        })) || []
    ) || [];
  const ungroupedNotes = result.notes || [];
  return [...folderNotes, ...ungroupedNotes];
}

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMeetingDateForNoteTitle(tsMs) {
  try {
    return new Date(Number(tsMs || Date.now())).toLocaleDateString("tr-TR");
  } catch {
    return new Date().toLocaleDateString();
  }
}

export default function MeMeetPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const {
    token,
    user,
    isAuthenticated,
    workspaces,
    myNotes,
    selectedNote,
    setSelectedNote,
    fetchMyNotes,
    getUserId,
    getDisplayName,
    getAvatarUrl,
    logout,
    isPro,
    tokenBalance,
    refreshTokenBalance,
  } = useAuth();
  const { language, changeLanguage, getTranslation, availableLanguages } = useLanguage();
  const { toastSuccess, toastError } = useToaster();

  const [requestedRoomId, setRequestedRoomId] = useState(roomId || "");
  const isFirstRender = useRef(true);

  // Handle room changes and update URL
  const handleRoomChange = useCallback((newRoomId) => {
    // Skip first render to avoid unnecessary navigation
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (newRoomId) {
      navigate(`/memeet/${newRoomId}`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const checklistSocketRef = useRef(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Socket connection for checklist real-time updates
  useEffect(() => {
    if (!SOCKET_URL) return;
    checklistSocketRef.current = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      transports: ["websocket"],
    });

    return () => {
      try {
        checklistSocketRef.current?.disconnect?.();
      } catch {
        // ignore
      } finally {
        checklistSocketRef.current = null;
      }
    };
  }, []);

  const emitChecklistRealtimeEvent = useCallback((workspaceID, checklistID, type, payload) => {
    const socket = checklistSocketRef.current;
    if (!socket || !socket.connected) return;

    const wsId = String(workspaceID || "").trim();
    const clId = String(checklistID || "").trim();
    if (!wsId) return;

    const message = {
      module: "checklist",
      workspaceID: wsId,
      checklistID: clId,
      type: type || "checklist:event",
      payload: payload || {},
      ts: Date.now(),
    };

    const rooms = [`checklist-workspace-${wsId}`, clId ? `checklist-${clId}` : ""].filter(Boolean);
    rooms.forEach((room) => {
      try {
        socket.emit("joinRoom", room);
      } catch {
        // ignore
      }
      try {
        socket.emit("message", { room, message });
      } catch (e) {
        console.error("[MeMeet] emit checklist realtime message error:", e);
      }
    });
  }, []);

  const lodosUserId = getUserId();
  const displayName = getDisplayName();
  const avatarUrl = getAvatarUrl();

  const emitNotepadRealtimeEvent = useCallback((noteId, type, payload) => {
    const socket = checklistSocketRef.current;
    if (!socket || !socket.connected) return;

    const nId = String(noteId || "").trim();
    const userId = String(lodosUserId || "").trim();
    if (!userId) return;

    const message = {
      module: "notepad",
      noteId: nId,
      userId,
      type: type || "notepad:updated",
      payload: payload || {},
      ts: Date.now(),
    };

    // User-specific room for notepad updates
    const room = `notepad-user-${userId}`;
    try {
      socket.emit("joinRoom", room);
    } catch {
      // ignore
    }
    try {
      socket.emit("message", { room, message });
    } catch (e) {
      console.error("[MeMeet] emit notepad realtime message error:", e);
    }
  }, [lodosUserId]);

  const handleGetLinkPreview = useCallback(
    (authToken, url) => {
      const resolvedToken = authToken || token;
      if (!resolvedToken || !url) return Promise.resolve(null);
      return Services.GetLinkPreview(resolvedToken, url);
    },
    [token]
  );

  const handleGetCollaborators = useCallback(() => {
    if (!token) return Promise.resolve({ data: { status: false, result: [] } });
    return Services.GetCollaborators(token);
  }, [token]);

  const handleMeMeetInviteUser = useCallback(
    ({ roomID, userID, email, nameLastname, notify }) => {
      if (!token) return Promise.resolve({ data: { status: false } });
      // notify: kullanicinin sectigi bildirim kanallari. Gecirilmezse backend
      // hicbir sey gondermez (davet yine olusur, link kopyalanabilir).
      return Services.MeMeetInviteUser(token, { roomID, userID, email, nameLastname, notify });
    },
    [token]
  );

  const handleMeMeetIsInvitedUser = useCallback(
    ({ roomID, userID, email }) => {
      if (!token) return Promise.resolve({ data: { status: false } });
      return Services.MeMeetIsInvitedUser(token, { roomID, userID, email });
    },
    [token]
  );

  const handleMeMeetRemoveInvitation = useCallback(
    ({ invitationCode }) => {
      if (!token) return Promise.resolve({ data: { status: false } });
      return Services.MeMeetRemoveInvitation(token, { invitationCode });
    },
    [token]
  );

  const handleMeMeetGetInvitations = useCallback(
    ({ roomID }) => {
      if (!token) return Promise.resolve({ data: { status: false, result: [] } });
      return Services.MeMeetGetInvitations(token, { roomID });
    },
    [token]
  );

  const formattedWorkspaces = useMemo(() => {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const mapped = list
      .map((p) => ({
        id: String(p?.projectID ?? p?.workspaceID ?? p?.id ?? "").trim(),
        title: String(p?.projectTitle ?? p?.title ?? p?.name ?? "").trim(),
      }))
      .filter((w) => w.id)
      .map((w) => ({ ...w, title: w.title || w.id }));

    const seen = new Set();
    const result = mapped.filter((w) => {
      if (seen.has(w.id)) return false;
      seen.add(w.id);
      return true;
    });
    return result;
  }, [workspaces]);

  const handleAddChatMessageToChecklist = useCallback(
    async ({ workspaceId, meetingId, message }) => {
      if (!token) return;
      const wsId = String(workspaceId || "").trim();
      const meetingTitle = String(meetingId || "").trim();
      const text = String(message?.text || "").trim();
      if (!wsId || !meetingTitle || !text) return;

      try {
        const resp = await Services.GetChecklistsByWorkspaceID(token, wsId);
        const lists = normalizeChecklistResponse(resp);

        const matching = lists
          .filter((c) => String(c?.title || "").trim() === meetingTitle)
          .sort((a, b) => Number(b?.createdDateTime || 0) - Number(a?.createdDateTime || 0));

        const active = matching.find((c) => !isTruthy(c?.is_archive ?? c?.isArchive ?? c?.isArchived ?? c?.archive ?? ""));
        let checklist = active || matching[0] || null;

        if (!checklist) {
          const resCreateChecklist = await Services.CreateChecklist(token, meetingTitle, false, wsId, "");
          if (!resCreateChecklist?.data?.status) throw new Error("Checklist create failed");
          const resp2 = await Services.GetChecklistsByWorkspaceID(token, wsId);
          const lists2 = normalizeChecklistResponse(resp2);
          const matching2 = lists2
            .filter((c) => String(c?.title || "").trim() === meetingTitle)
            .sort((a, b) => Number(b?.createdDateTime || 0) - Number(a?.createdDateTime || 0));
          checklist = matching2[0] || null;
        }

        if (!checklist) throw new Error("Checklist not found/created");

        const checklistId = String(checklist?.checklistID ?? checklist?.id ?? "").trim();
        const checklistName = String(checklist?.title || meetingTitle).trim();
        const description = String(checklist?.description || "").trim();
        const isQueueRestricted = isTruthy(checklist?.isQueueRestricted);
        if (!checklistId) throw new Error("Checklist id missing");

        const existingElements = Array.isArray(checklist?.elements) ? checklist.elements : [];
        const normalizedElements = existingElements.map((el, idx) => ({
          elementId: el?.elementId,
          elementDescription: el?.elementDescription || el?.elementTitle || "",
          elementResponsibleBy: el?.elementResponsibleBy || "",
          elementAssignedBy: el?.elementAssignedBy || "",
          elementAssignedByName: el?.elementAssignedByName || "",
          elementEndDateTime: el?.elementEndDateTime || "",
          elementStatus: String(el?.elementStatus || "1"),
          elementPriority: Number(el?.elementPriority || 2),
          elementOrder: typeof el?.elementOrder === "number" ? el.elementOrder : idx,
          elementParentId: el?.elementParentId ?? null,
        }));

        const nextElements = [
          ...normalizedElements,
          {
            elementDescription: text,
            elementResponsibleBy: "",
            elementAssignedBy: lodosUserId ? String(lodosUserId) : "",
            elementAssignedByName: displayName ? String(displayName) : "",
            elementEndDateTime: "",
            elementStatus: "1",
            elementPriority: 2,
            elementOrder: normalizedElements.length,
            elementParentId: null,
          },
        ];

        const resUpdate = await Services.UpdateChecklist(token, checklistName, nextElements, description, checklistId, isQueueRestricted);
        if (!resUpdate?.data?.status) throw new Error("Checklist update failed");

        emitChecklistRealtimeEvent(wsId, checklistId, "checklist:items-updated", {
          checklistID: checklistId,
          workspaceID: wsId,
          source: "memeet",
        });
        // Show the message text that was added, truncated if too long
        const preview = text.length > 40 ? text.substring(0, 40) + "..." : text;
        toastSuccess(`"${preview}" ${getTranslation("memeetWasAddedIntoChecklistNamed", { checklistName })}`);
        return { checklistId, checklistName };
      } catch (error) {
        console.error("Error adding to checklist:", error);
        toastError(getTranslation("memeetChecklistAddFailed"));
      }
    },
    [token, lodosUserId, displayName, emitChecklistRealtimeEvent, toastSuccess, toastError, getTranslation]
  );

  const handleAddChatMessageToNotes = useCallback(
    async ({ meetingId, meetingStartedAt, message }) => {
      if (!token) return;

      const meetingTitle = String(meetingId || "").trim();
      const text = String(message?.text || "").trim();
      if (!meetingTitle || !text) return;

      try {
        const dateStr = formatMeetingDateForNoteTitle(meetingStartedAt);
        const noteTitle = `${dateStr} ${meetingTitle}`.trim();

        const resNotes = await Services.GetMyNotes(token);
        const allNotes = normalizeNoteList(resNotes);
        const existing = allNotes.find((n) => String(n?.title || "").trim() === noteTitle) || null;

        let note = existing;
        if (!note) {
          const resCreate = await Services.CreateNote(token, "");
          if (!resCreate?.data?.status) throw new Error("Note create failed");
          const noteId = resCreate.data.result.noteId;
          const resTitle = await Services.NoteTitleUpdate(token, noteId, noteTitle);
          if (!resTitle?.data?.status) throw new Error("Note title update failed");

          const resNotes2 = await Services.GetMyNotes(token);
          const allNotes2 = normalizeNoteList(resNotes2);
          note = allNotes2.find((n) => String(n?.noteId) === String(noteId)) || { noteId, title: noteTitle, content: "" };
        }

        const noteId = String(note?.noteId || "").trim();
        if (!noteId) throw new Error("Note not found/created");

        let currentContent = "";
        if (typeof note?.content === "string") {
          currentContent = note.content;
        } else if (note?.content && typeof note.content === "object" && Array.isArray(note.content.blocks)) {
          try {
            const draftToHtml = require("draftjs-to-html").default;
            currentContent = draftToHtml(note.content);
          } catch {
            currentContent = "";
          }
        }
        const safe = escapeHtml(text).replace(/\n/g, "<br/>");
        const nextContent = `${currentContent || ""}<p>• ${safe}</p>`;

        const resUpdate = await Services.NoteUpdate(token, noteId, nextContent);
        if (!resUpdate?.data?.status) throw new Error("Note update failed");

        // Emit socket event for real-time sync
        emitNotepadRealtimeEvent(noteId, "notepad:updated", {
          noteId,
          noteTitle,
          source: "memeet",
        });

        try {
          fetchMyNotes?.();
        } catch {
          // ignore
        }

        // Touch note to add to recent notes
        try {
          const touchRes = await Services.TouchToNote(token, noteId);
          console.log("[MeMeet] TouchToNote result:", touchRes?.data);
        } catch (err) {
          console.error("[MeMeet] TouchToNote error:", err);
        }

        try {
          const currentSelectedId = selectedNote?.noteId;
          if (currentSelectedId && String(currentSelectedId) === String(noteId)) {
            setSelectedNote?.({ ...(selectedNote || {}), noteId, title: noteTitle, content: nextContent });
          }
        } catch {
          // ignore
        }

        toastSuccess(`"${text.slice(0, 30)}${text.length > 30 ? "..." : ""}" ${getTranslation("memeetWasAddedIntoYourNotes")}`);
        return { noteId, noteTitle };
      } catch (error) {
        console.error("Error adding to notes:", error);
        toastError(getTranslation("somethingWentWrong"));
      }
    },
    [token, fetchMyNotes, selectedNote, setSelectedNote, toastSuccess, toastError, getTranslation, emitNotepadRealtimeEvent]
  );

  if (!isAuthenticated) {
    return null;
  }

  // SFU geçişi opt-in: ?lk=1 query veya localStorage.memeetUseLiveKit="1"
  // SFU artık DEFAULT. Eski WebRTC mesh'e opt-out: ?lk=0 veya localStorage="0".
  // Tercih sessionStorage'a sticky'lenir (sekme bazlı).
  const useLiveKit = (() => {
    try {
      if (typeof window === "undefined") return true;
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("lk") === "0") {
        try { window.sessionStorage?.setItem("memeetUseLiveKit", "0"); } catch {}
        return false;
      }
      if (qs.get("lk") === "1") {
        try { window.sessionStorage?.setItem("memeetUseLiveKit", "1"); } catch {}
        return true;
      }
      const ss = window.sessionStorage?.getItem("memeetUseLiveKit");
      if (ss === "0") return false;
      if (ss === "1") return true;
      const ls = window.localStorage?.getItem("memeetUseLiveKit");
      if (ls === "0") return false;
      return true;
    } catch {
      return true;
    }
  })();

  return (
    <div className="memeet-page">
      <MeMeetApp
        token={token}
        lodosUserId={lodosUserId}
        nickname={displayName}
        displayName={displayName}
        avatarUrl={avatarUrl}
        language={language}
        changeLanguage={changeLanguage}
        availableLanguages={availableLanguages}
        getTranslation={getTranslation}
        requestedRoomId={requestedRoomId}
        onRequestedRoomIdHandled={() => setRequestedRoomId("")}
        getLinkPreview={handleGetLinkPreview}
        workspaces={formattedWorkspaces}
        onAddChatMessageToChecklist={handleAddChatMessageToChecklist}
        onAddChatMessageToNotes={handleAddChatMessageToNotes}
        onGetCollaboratorsService={handleGetCollaborators}
        onInviteUserService={handleMeMeetInviteUser}
        onIsInvitedUserService={handleMeMeetIsInvitedUser}
        onRemoveInvitationService={handleMeMeetRemoveInvitation}
        onGetInvitationsService={handleMeMeetGetInvitations}
        onLogout={logout}
        onRoomChange={handleRoomChange}
        isPro={isPro}
        tokenBalance={tokenBalance}
        refreshTokenBalance={refreshTokenBalance}
        useLiveKit={useLiveKit}
      />
    </div>
  );
}
